import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./PasiDermaDetails.css";

const getDifficultyLevel = (s) => {
  if (s < 0.1) return { text: "No Psoriasis", color: "#4caf50" };
  if (s < 10) return { text: "Mild Psoriasis", color: "#8bc34a" };
  if (s < 20) return { text: "Moderate Psoriasis", color: "#ff9800" };
  return { text: "Severe Psoriasis", color: "#f44336" };
};

const getReason = (s) => {
  if (s < 0.1) return "No signs of Psoriasis";
  if (s < 10) return "You have mild psoriasis";
  if (s < 20) return "You have moderate psoriasis";
  return "You have severe psoriasis";
};

const PasiDermaDetails = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";
  const authToken = urlParams.get("authToken") || "";
  const uid = urlParams.get("uid") || "";
  const type = urlParams.get("type") || "";

  const [scoreData, setScoreData] = useState(null);
  const [reportUrl, setReportUrl] = useState("");
  const [logdate, setLogDate] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [nextDate, setNextDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  useEffect(() => {
    lifestyleApi
      .fetchPasiScore()
      .then((res) => {
        const data = res?.data?.data;
        if (data) {
          setScoreData(data.scoreResponse && data.scoreResponse.length ? data.scoreResponse : null);
          setReportUrl(data.reportUrl || "");
          setLogDate(data.logDate || "");
        }
      })
      .catch((err) => console.error("fetchPasiScore:", err))
      .finally(() => setLoading(false));
  }, [type]);

  useEffect(() => {
    if (logdate) {
      const next = new Date(Date.parse(logdate) + 30 * 24 * 60 * 60 * 1000);
      setNextDate(next);
      if (next > new Date()) setShowPopup(true);
    }
  }, [logdate]);

  const formatDate = (d) => {
    if (!d) return "";
    const obj = d instanceof Date ? d : new Date(d);
    return `${obj.getDate()}-${obj.toLocaleString("default", { month: "short" })}-${obj.getFullYear().toString().slice(-2)}`;
  };

  const buildQs = () =>
    `uuid=${uuid}&uid=${uid}&goqiiUserId=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&authToken=${authToken}&theme=${theme}&lang=${lang}`;

  const handleTakeClick = () => {
    if (showPopup) {
      setShowBlockedModal(true);
    } else {
      window.location.href = `/pasi-score?${buildQs()}`;
    }
  };

  const latestScore = scoreData ? scoreData[0]?.score : null;
  const difficulty = latestScore != null ? getDifficultyLevel(latestScore) : { text: "", color: "" };
  const reason = latestScore != null ? getReason(latestScore) : "";

  return (
    <>
      <section className="pasid-page">
        <div className="pasid-container">
          {loading ? (
            <div className="pasid-loading">{t("common.loading", "Loading...")}</div>
          ) : (
            <>
              <p className="pasid-description">
                {!type
                  ? t("pasiDetails.description", "Psoriasis Area and Severity Index (PASI) is a questionnaire to evaluate the severity of psoriasis.")
                  : reason}
              </p>

              {!type && (
                <div className="pasid-take-btn-wrap">
                  <button
                    type="button"
                    className="pasid-take-btn"
                    onClick={handleTakeClick}
                  >
                    {t("pasiDetails.takeBtn", "TAKE A PASI")}
                  </button>
                </div>
              )}

              {scoreData && (
                <>
                  <label className="pasid-label">{t("pasiDetails.previousLabel", "Previous PASI")}</label>
                  <div className="pasid-score-card">
                    <div style={{ flex: 1 }}>
                      <p className="pasid-difficulty-text" style={{ color: difficulty.color || "var(--text-primary)" }}>
                        {difficulty.text}
                      </p>
                      <div className="pasid-difficulty-bar" style={{ background: difficulty.color || "#ccc" }} />
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <p className="pasid-score-value">{latestScore}</p>
                      <p className="pasid-score-max">(Out of 72)</p>
                    </div>
                    {reportUrl ? (
                      <a
                        href={`https://goqii.com/app?fsn=244&fssn=0&fai=${encodeURIComponent(JSON.stringify({ url: reportUrl, title: "PASI-Report" }))}`}
                        className="pasid-report-link"
                      >
                        {t("pasiDetails.viewReport", "View Report")}
                      </a>
                    ) : null}
                  </div>

                  <div className="pasid-view-all-wrap">
                    <a
                      href={`https://goqii.com/app?fsn=242&fssn=5&fai=${encodeURIComponent(JSON.stringify({ url: `https://lifestyle1.goqii.com/self-stats?uuid=${uuid}`, title: "Flareup" }))}`}
                      className="pasid-view-all-link"
                    >
                      {t("pasiDetails.viewAll", "View All Previous PASI")}
                    </a>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {showBlockedModal && (
        <div className="pasid-popup-overlay">
          <div className="pasid-popup-box">
            <p className="pasid-popup-text">
              {t("pasiDetails.completedOn", "You have completed a PASI on")}{" "}
              <strong>{formatDate(logdate)}</strong>.{" "}
              {t("pasiDetails.nextOn", "The next one is on")} {formatDate(nextDate)}.
            </p>
            <button
              type="button"
              className="pasid-take-btn"
              onClick={() => setShowBlockedModal(false)}
            >
              {t("pasiDetails.ok", "Ok")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PasiDermaDetails;
